import { useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { useSubmitReport, useUploadMedia, type BloggerPlacement } from "@/shared/api/wb";

export function ReportModal({ bloggerId, placement, onClose }: {
  bloggerId: string; placement: BloggerPlacement; onClose: () => void;
}) {
  const [link, setLink] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadMedia();
  const submit = useSubmitReport();

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    upload.mutate({ file, kind: "image" }, { onSuccess: (m) => setScreenshotUrl(m.url) });
  }

  function onSubmit() {
    if (!link.trim()) return;
    submit.mutate(
      { bloggerId, placementId: placement.id, link: link.trim(), screenshotUrl },
      { onSuccess: onClose },
    );
  }

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center" style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-lg fade-up p-6 grid gap-4" style={{ width: "min(480px, 92vw)" }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">Отчёт о выходе</div>
            <h3 className="font-display font-extrabold text-[19px] tracking-tight">{placement.campaign_product}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6"><X size={16} /></button>
        </div>

        <label className="grid gap-1.5">
          <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">Ссылка на пост</span>
          <input
            type="url"
            required
            autoFocus
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://t.me/channel/123"
            className="h-10 px-3 rounded-lg border border-ink-12 bg-white text-[13.5px] outline-none focus:border-ink"
          />
        </label>

        <label className="grid gap-1.5">
          <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">Скриншот статистики</span>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
          {screenshotUrl ? (
            <div className="relative rounded-lg border border-ink-12 overflow-hidden">
              <img src={screenshotUrl} alt="Скриншот статистики" className="w-full max-h-[220px] object-cover" />
              <button
                onClick={() => setScreenshotUrl(null)}
                className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-md bg-black/60 text-white hover:bg-black/80"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="h-24 rounded-lg border-2 border-dashed border-ink-20 grid place-items-center gap-1.5 text-ink-40 hover:border-ink/40 hover:text-ink transition-colors disabled:opacity-50"
            >
              {upload.isPending ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
              <span className="text-[12px] font-display font-semibold">{upload.isPending ? "Загружаем…" : "Прикрепить изображение"}</span>
            </button>
          )}
        </label>

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="h-9 px-4 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink/40">Отмена</button>
          <button
            onClick={onSubmit}
            disabled={!link.trim() || submit.isPending}
            className="h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[13px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] disabled:opacity-50"
          >
            {submit.isPending ? "Отправляем…" : "Отправить на проверку"}
          </button>
        </div>
      </div>
    </div>
  );
}
